import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { createProject, listProjects, listTerms } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDateShort } from "../lib/format";
import {
  PROJECT_COLORS,
  PROJECT_ICONS,
  PROJECT_PRIORITIES,
  PROJECT_STATUSES,
  projectPriorityMeta,
  projectStatusMeta,
} from "../lib/constants";
import { PageBar } from "../components/Layout";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { Modal } from "../components/Modal";
import { ProjectIcon } from "../components/ProjectIcon";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import type { ProjectRow } from "../lib/types";

function progressPct(p: ProjectRow): number {
  const total = p.task_count ?? 0;
  if (!total) return 0;
  return Math.round(((p.done_count ?? 0) / total) * 100);
}

const PROJECT_SORT: Record<string, SortAccessor<ProjectRow>> = {
  name: (p) => p.name,
  status: (p) => projectStatusMeta(p.status).label,
  priority: (p) => projectPriorityMeta(p.priority).label,
  start: (p) => p.start_date,
  due: (p) => p.end_date,
  progress: (p) => progressPct(p),
  owner: (p) => p.lead_name,
};

export function ProjectsPage() {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [view, setView] = useState<"list" | "board">("list");
  const [q, setQ] = useState("");
  // Terms are still resolved for the per-project term TAG (display only); the
  // list-level term FILTER was removed.
  const termsApi = useApi((signal) => listTerms(signal), []);
  const terms = termsApi.data?.terms ?? [];
  const termName = (code: string) => terms.find((t) => t.code === code)?.name ?? code;
  const { data, loading, error, reload } = useApi(
    (signal) => listProjects(false, signal),
    [],
  );
  const all = data?.projects ?? [];
  const projects = q.trim()
    ? all.filter((p) =>
        `${p.name} ${p.key} ${p.description ?? ""}`.toLowerCase().includes(q.trim().toLowerCase()),
      )
    : all;
  const { rows: sortedProjects, sort, toggle } = useTableSort(projects, PROJECT_SORT);

  // Portfolio summary (Asana/Monday-style overview): counts by status tone and
  // an aggregate completion across all visible projects.
  const onTrack = projects.filter((p) => projectStatusMeta(p.status).tone === "ok").length;
  const atRisk = projects.filter((p) => {
    const tone = projectStatusMeta(p.status).tone;
    return tone === "warn" || tone === "danger";
  }).length;
  const avgPct = projects.length
    ? Math.round(projects.reduce((sum, p) => sum + progressPct(p), 0) / projects.length)
    : 0;

  return (
    <>
      <PageBar
        crumbs="Projects"
        actions={
          <button className="btn primary" onClick={() => setShowCreate(true)}>
            + New project
          </button>
        }
      />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>
              Projects {all.length ? <span className="count-chip">{all.length}</span> : null}
            </h1>
            <div className="sub">Manage all projects within this workspace.</div>
          </div>
          <div className="page-head-actions">
            <div className="view-switch" role="group" aria-label="View">
              <button
                className={`view-switch-btn${view === "list" ? " active" : ""}`}
                onClick={() => setView("list")}
                aria-pressed={view === "list"}
              >
                List
              </button>
              <button
                className={`view-switch-btn${view === "board" ? " active" : ""}`}
                onClick={() => setView("board")}
                aria-pressed={view === "board"}
              >
                Board
              </button>
            </div>
            <input
              className="search-inline"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects…"
              aria-label="Search projects"
            />
          </div>
        </div>

        {!loading && !error && all.length ? (
          <div className="portfolio-stats">
            <div className="stat-card">
              <span className="stat-num">{projects.length}</span>
              <span className="stat-label">Projects</span>
            </div>
            <div className="stat-card">
              <span className="stat-num ok">{onTrack}</span>
              <span className="stat-label">On track</span>
            </div>
            <div className="stat-card">
              <span className="stat-num warn">{atRisk}</span>
              <span className="stat-label">At risk / off track</span>
            </div>
            <div className="stat-card">
              <span className="stat-num">{avgPct}%</span>
              <span className="stat-label">Avg. completion</span>
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="card"><div className="card-body tight"><Loading label="Loading projects…" /></div></div>
        ) : error ? (
          <div className="card"><div className="card-body tight"><ErrorState error={error} onRetry={reload} /></div></div>
        ) : projects.length === 0 ? (
          <div className="card"><div className="card-body tight">
            <EmptyState>
              {q.trim()
                ? "No projects match your search."
                : "No projects yet. Create one to start planning work."}
            </EmptyState>
          </div></div>
        ) : view === "board" ? (
          <div style={{ marginTop: 16 }}>
            <ProjectsBoard projects={projects} onOpen={(id) => navigate(`/projects/${id}`)} termName={termName} />
          </div>
        ) : (
          <div className="card" style={{ marginTop: 16 }}>
          <div className="card-body tight">
            {(
              <table className="data projects-table">
                <thead>
                  <tr>
                    <SortTh field="name" sort={sort} onSort={toggle}>Project name</SortTh>
                    <SortTh field="status" sort={sort} onSort={toggle}>Status</SortTh>
                    <SortTh field="priority" sort={sort} onSort={toggle}>Priority</SortTh>
                    <SortTh field="start" sort={sort} onSort={toggle}>Start</SortTh>
                    <SortTh field="due" sort={sort} onSort={toggle}>Due</SortTh>
                    <SortTh field="progress" sort={sort} onSort={toggle}>Task progress</SortTh>
                    <SortTh field="owner" sort={sort} onSort={toggle}>Owner</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {sortedProjects.map((p) => {
                    const st = projectStatusMeta(p.status);
                    const pr = projectPriorityMeta(p.priority);
                    const pct = progressPct(p);
                    return (
                      <tr
                        key={p.id}
                        className="clickable"
                        role="button"
                        aria-label={`Open project ${p.name}`}
                        onClick={() => navigate(`/projects/${p.id}`)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/projects/${p.id}`);
                          }
                        }}
                      >
                        <td>
                          <div className="proj-name">
                            <ProjectIcon icon={p.icon ?? null} color={p.color ?? null} name={p.name} />
                            <div className="proj-name-text">
                              <strong>{p.name}</strong>
                              <span className="proj-sub">
                                <span className="mono faint">{p.key}</span>
                                {p.term ? <span className="tag tag-info">{termName(p.term)}</span> : null}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`tag tag-${st.tone}`}>{st.label}</span>
                        </td>
                        <td>
                          <span className={`tag tag-${pr.tone}`}>{pr.label}</span>
                        </td>
                        <td className="num faint">{p.start_date ? formatDateShort(p.start_date) : "—"}</td>
                        <td className="num faint">{p.end_date ? formatDateShort(p.end_date) : "—"}</td>
                        <td>
                          <div className="progress-cell">
                            <div className="progress-track">
                              <div
                                className={`progress-fill${pct === 100 ? " done" : ""}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="progress-pct">{pct}%</span>
                          </div>
                        </td>
                        <td>
                          <span className="owner-chip" title={p.lead_name ?? "Unassigned"}>
                            <span className="avatar xs">{initials(p.lead_name)}</span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          </div>
        )}
      </div>

      {showCreate ? (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            navigate(`/projects/${id}`);
          }}
        />
      ) : null}
    </>
  );
}

/** Portfolio board: projects grouped into columns by lifecycle status — the
 *  Monday/Asana "grouped board" view. Read-only (click a card to open); status
 *  is changed inside a project, not by dragging here. Empty status columns are
 *  hidden so the board stays tight. */
function ProjectsBoard({
  projects,
  onOpen,
  termName,
}: {
  projects: ProjectRow[];
  onOpen: (id: string) => void;
  termName: (code: string) => string;
}) {
  const columns = PROJECT_STATUSES.map((s) => ({
    ...s,
    items: projects.filter((p) => (p.status ?? "on_track") === s.code),
  })).filter((col) => col.items.length > 0);

  return (
    <div className="portfolio-board">
      {columns.map((col) => {
        const tone = projectStatusMeta(col.code).tone;
        return (
          <div className="portfolio-col" key={col.code}>
            <div className="portfolio-col-head">
              <span className={`status-dot tone-${tone}`} aria-hidden="true" />
              <span className="portfolio-col-title">{col.label}</span>
              <span className="faint">{col.items.length}</span>
            </div>
            <div className="portfolio-col-body">
              {col.items.map((p) => {
                const pr = projectPriorityMeta(p.priority);
                const pct = progressPct(p);
                return (
                  <button
                    type="button"
                    key={p.id}
                    className="portfolio-card"
                    onClick={() => onOpen(p.id)}
                    aria-label={`Open project ${p.name}`}
                  >
                    <div className="proj-name">
                      <ProjectIcon icon={p.icon ?? null} color={p.color ?? null} name={p.name} />
                      <div className="proj-name-text">
                        <strong>{p.name}</strong>
                        <span className="proj-sub">
                          <span className="mono faint">{p.key}</span>
                          {p.term ? <span className="tag tag-info">{termName(p.term)}</span> : null}
                        </span>
                      </div>
                    </div>
                    <div className="portfolio-card-meta">
                      <span className={`tag tag-${pr.tone}`}>{pr.label}</span>
                      <span className="owner-chip" title={p.lead_name ?? "Unassigned"}>
                        <span className="avatar xs">{initials(p.lead_name)}</span>
                      </span>
                    </div>
                    <div className="progress-cell">
                      <div className="progress-track">
                        <div
                          className={`progress-fill${pct === 100 ? " done" : ""}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="progress-pct">{pct}%</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function initials(name?: string | null): string {
  if (!name) return "—";
  return (
    name
      .trim()
      .split(/\s+/)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .slice(0, 2)
      .join("") || "—"
  );
}

function CreateProjectModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState(PROJECT_ICONS[0]!);
  const [color, setColor] = useState(PROJECT_COLORS[0]!);
  const [status, setStatus] = useState("on_track");
  const [priority, setPriority] = useState("medium");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [term, setTerm] = useState("");
  const termsApi = useApi((signal) => listTerms(signal), []);
  const terms = termsApi.data?.terms ?? [];
  const { run, pending, error } = useMutation(createProject);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!key.trim() || !name.trim()) return;
    const toSec = (d: string) => (d ? Math.floor(new Date(d + "T00:00:00Z").getTime() / 1000) : undefined);
    const start = toSec(startDate);
    const end = toSec(endDate);
    const res = await run({
      key: key.trim().toUpperCase(),
      name: name.trim(),
      icon,
      color,
      status,
      priority,
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(start !== undefined ? { start_date: start } : {}),
      ...(end !== undefined ? { end_date: end } : {}),
      ...(term ? { term } : {}),
    });
    if (res) onCreated(res.project.id);
  }

  return (
    <Modal
      title="New project"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="create-project-form"
            disabled={pending || !key.trim() || !name.trim()}
          >
            {pending ? <Spinner label="Creating…" /> : "Create project"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="create-project-form" onSubmit={onSubmit}>
        {/* Icon + color live preview */}
        <div className="icon-picker">
          <div className="icon-preview">
            <ProjectIcon icon={icon} color={color} name={name || "P"} size={44} />
          </div>
          <div className="icon-picker-choices">
            <div className="swatch-row" role="group" aria-label="Icon">
              {PROJECT_ICONS.map((ic, i) => (
                <button
                  type="button"
                  key={ic}
                  className={`swatch icon-swatch${ic === icon ? " active" : ""}`}
                  aria-label={`Icon ${i + 1}`}
                  aria-pressed={ic === icon}
                  onClick={() => setIcon(ic)}
                >
                  {ic}
                </button>
              ))}
            </div>
            <div className="swatch-row" role="group" aria-label="Color">
              {PROJECT_COLORS.map((cl, i) => (
                <button
                  type="button"
                  key={cl}
                  className={`swatch color-swatch${cl === color ? " active" : ""}`}
                  style={{ background: cl }}
                  aria-label={`Color ${i + 1}`}
                  aria-pressed={cl === color}
                  onClick={() => setColor(cl)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="row">
          <label className="field" style={{ flex: "0 0 120px" }}>
            <span className="label">Key</span>
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value.toUpperCase())}
              placeholder="ENG"
              autoFocus
              required
            />
          </label>
          <label className="field">
            <span className="label">Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Engineering"
              required
            />
          </label>
        </div>

        <label className="field">
          <span className="label">Description</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
          />
        </label>

        <div className="row">
          <label className="field">
            <span className="label">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {PROJECT_STATUSES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {PROJECT_PRIORITIES.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="row">
          <label className="field">
            <span className="label">Start date</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Due date</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
        </div>

        {terms.length ? (
          <label className="field">
            <span className="label">Academic term</span>
            <select value={term} onChange={(e) => setTerm(e.target.value)}>
              <option value="">None</option>
              {terms.map((t) => (
                <option key={t.id} value={t.code}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </form>
    </Modal>
  );
}
